import React from 'react';
import { SimpleTable, Spacing, Stack } from '@doveit/bricks';
import { SerpJobApplication } from '../../providers/api/dtos';
import {
  JobApplicationDuplicatesNoticeData,
  JobApplicationNoticeData,
  Notice,
  Severity,
} from '../../providers/api/dtos/notice';
import ViewJobApplicationRow from '../../job-application/containers/view-job-application-row/ViewJobApplicationRow';
import ViewJobApplicationCard from '../../job-application/containers/view-job-application-notice-card/ViewJobApplicationCard';
import { BuildJobApplicationNoticeCtx } from './types';

function renderJobApplications(
  serpJobApplications: SerpJobApplication[],
  isMobile: boolean,
  mutateNoticeData: VoidFunction,
) {
  if (isMobile) {
    return (
      <Stack gap={150}>
        {serpJobApplications.map((serpJobApplication) => (
          <ViewJobApplicationCard
            key={serpJobApplication.jobApplication.id}
            serpJobApplication={serpJobApplication}
            onJobApplicationUpdate={mutateNoticeData}
          />
        ))}
      </Stack>
    );
  }

  return (
    <SimpleTable>
      <SimpleTable.Body>
        {serpJobApplications.map((serpJobApplication) => (
          <SimpleTable.Row
            key={serpJobApplication.jobApplication.id}
            asChild
          >
            <ViewJobApplicationRow
              serpJobApplication={serpJobApplication}
              onJobApplicationUpdate={mutateNoticeData}
            />
          </SimpleTable.Row>
        ))}
      </SimpleTable.Body>
    </SimpleTable>
  );
}

export function buildJobApplicationDuplicatesNotice(
  noticeData: JobApplicationDuplicatesNoticeData,
  ctx: BuildJobApplicationNoticeCtx,
): Notice {
  const {
    data: {
      sameApplicantJobApplications,
      similarApplicants,
      similarApplicantsJobApplications,
    },
  } = noticeData;
  const { applicant, isMobile, mutateNoticeData } = ctx;

  const sameApplicantSerpJobApplications = sameApplicantJobApplications.map((jobApplication) => ({
    jobApplication,
    applicant,
  } as SerpJobApplication));

  const applicantsById = new Map(similarApplicants.map((similarApplicant) => [similarApplicant.id, similarApplicant]));
  const similarApplicantsSerpJobApplications = similarApplicantsJobApplications.reduce<SerpJobApplication[]>((acc, jobApplication) => {
    const similarApplicant = applicantsById.get(jobApplication.applicantId);

    if (!similarApplicant) return acc;

    acc.push({
      jobApplication,
      applicant: similarApplicant,
    } as SerpJobApplication);

    return acc;
  }, []);

  return {
    type: noticeData.type,
    severity: Severity.WARNING,
    priority: 1,
    message: 'Possibili candidature collegate',
    children: (
      <>
        {sameApplicantSerpJobApplications.length > 0 && (
          <>
            Altre candidature dello stesso candidato
            <Spacing margin={[200, 0, 0]}>
              {renderJobApplications(sameApplicantSerpJobApplications, isMobile, mutateNoticeData)}
            </Spacing>
          </>
        )}
        {sameApplicantSerpJobApplications.length > 0 && similarApplicantsSerpJobApplications.length > 0 && (
          <Spacing margin={[250, 0, 0]} />
        )}
        {similarApplicantsSerpJobApplications.length > 0 && (
          <>
            Candidature di possibili candidati duplicati
            <Spacing margin={[200, 0, 0]}>
              {renderJobApplications(similarApplicantsSerpJobApplications, isMobile, mutateNoticeData)}
            </Spacing>
          </>
        )}
      </>
    ),
  };
}

export function buildJobApplicationNotice(noticeData: JobApplicationNoticeData, ctx: BuildJobApplicationNoticeCtx): Notice {
  switch (noticeData.type) {
    case 'JOB_APPLICATION_DUPLICATES':
      return buildJobApplicationDuplicatesNotice(noticeData, ctx);
    default:
      throw new Error('Unknown job application notice type');
  }
}
